import { observer } from 'mobx-react-lite'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { type StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { type InstructorAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorAssignmentCubit'
import {
  BreakoutUserAvatar,
  BreakoutUserAvatarStack,
} from 'components/breakout/BreakoutUserAvatar'
import { useBreakoutUser } from 'hooks/profile'
import { forwardRef, memo, useEffect, useMemo, useRef, useId } from 'react'
import { ScheduleRoomStateButton } from 'pages/student/assignment/dialogs/ScheduleRoomStateButton'
import { CalendarIcon } from 'components/icons/Calendar'
import {
  type ChatMessage,
  type SystemMessage,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { SpinnerCentered } from 'components/Spinner'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { SendIcon } from 'components/icons/Send'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { CheckIcon } from 'components/icons/Check'
import { DoubleCheckIcon } from 'components/icons/DoubleCheck'
import { type PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { HyperlinkedText } from 'components/breakout/HyperlinkedText'
import {
  ScheduledDayOfMonthOnYear,
  ScheduledDayOfWeekAtTime,
} from 'pages/student/assignment/ScheduledTimeParts'
import { useTranslationTyped } from 'i18n/i18n'
import { useOnVisible } from 'hooks/useOnVisible'
import { ChatNudgeMessage } from 'pages/meeting/chat/ChatNudgeMessage'
import { ChatRoomStartedSyntheticMessage } from 'pages/meeting/chat/ChatRoomStartedSyntheticMessage'

type GroupChatDialogProps =
  | {
      mode: 'normal'
      cubit: StudentAssignmentCubit
    }
  | {
      mode: 'read-only'
      cubit: InstructorAssignmentCubit
      roomId: string
    }

export const GroupChatDialog = observer(function GroupChatDialog(
  props: GroupChatDialogProps
) {
  const { tt } = useTranslationTyped()
  const currentUser = useBreakoutUser()
  const chatBoxScrollRef = useRef<boolean>(false)
  const textInputRef = useRef<HTMLInputElement>(null)
  const headingId = useId()

  // Extract data from cubits based on mode
  // do not use useMemo here as there are a bunch of observables inside
  const { roomState, chat, assignment, section, scheduledAt, roomStateStatus } =
    (() => {
      if (props.mode === 'normal') {
        const { cubit } = props
        return {
          roomState: cubit.roomState,
          chat: cubit.chat,
          assignment: cubit.assignment,
          section: cubit.section,
          scheduledAt: cubit.libraryObject.scheduledAtDate?.toJSDate(),
          roomStateStatus: cubit.roomStateStatus,
        }
      } else {
        const { cubit, roomId } = props
        const room = cubit.roomStates.models.find((r) => r.id === roomId)
        if (!room) {
          throw new Error(`Room ${roomId} not found`)
        }
        const roomMemberIds = room.userIds
        const status = room.getRoomStateStatus(
          cubit.assignment.getEffectiveExpiresAtForUsers(roomMemberIds),
          cubit.section.data.sectionState,
          cubit.slideDeckSlides.length,
          cubit.assignment.data.assignmentState,
          cubit.roomStateEngagements.models.some(
            (e) => e.data.roomId === room.id
          ),
          cubit.assignment.hasFutureIndividualDeadlineForAnyUser(roomMemberIds)
        )
        return {
          roomState: room,
          chat: cubit.getChatController(roomId),
          assignment: cubit.assignment,
          section: cubit.section,
          scheduledAt: room.data.scheduledAt,
          roomStateStatus: status,
        }
      }
    })()

  const isReadOnly = props.mode === 'read-only'

  const isStudentInRoom = roomState.users.some((u) => u.id === currentUser.uid)

  const canManageAssignment = useMemo(() => {
    if (isReadOnly) {
      return false
    }
    const studentCubit = props.cubit
    return (
      currentUser.isCorre ||
      studentCubit.currentUserIsInstructor ||
      currentUser.isAssistantOfInstructor(section.data.instructorUserId)
    )
  }, [currentUser, section.data.instructorUserId, isReadOnly, props])

  const canSchedule = (canManageAssignment || isStudentInRoom) && !isReadOnly

  const showLargeScheduleButton = canSchedule && !scheduledAt

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const text = textInputRef.current?.value
    if (!text) return

    chat.sendMessage(text)
    textInputRef.current.value = ''
  }

  const chatDisabled: boolean = useMemo(() => {
    switch (roomStateStatus) {
      case RoomStateStatus.live:
      case RoomStateStatus.scheduled:
      case RoomStateStatus.mustSchedule:
      case RoomStateStatus.mustScheduleSuspended:
      case RoomStateStatus.suspended:
        return false
      case RoomStateStatus.completed:
      case RoomStateStatus.abandoned:
      case RoomStateStatus.queuedForGrading:
      case RoomStateStatus.canceled:
      case RoomStateStatus.expired:
        return true
    }
  }, [roomStateStatus])

  let disableScheduleRoom = false
  let disabledScheduleTooltip: string = ''

  if (isReadOnly) {
    disableScheduleRoom = true
    disabledScheduleTooltip =
      'Scheduling is available in the group management dialog'
  } else if (!isReadOnly && assignment.data.forbidStudentGroupScheduling) {
    disableScheduleRoom = true
    disabledScheduleTooltip =
      tt.student_assignment.schedule_group_disabled_by_instructor()
  }

  if (!roomState.isLoaded) {
    return (
      <DialogWrapper headingId={headingId}>
        <SpinnerCentered />
      </DialogWrapper>
    )
  }

  return (
    <DialogWrapper headingId={headingId}>
      <div className="flex h-full w-full flex-grow flex-col gap-4 px-9">
        <div className="flex flex-row items-center justify-between px-7">
          {/* room name & user avatars */}
          <div className="flex flex-col items-start gap-1">
            <h1 id={headingId} className="text-title-large">
              {roomState.roomStateName}
            </h1>
            <BreakoutUserAvatarStack
              users={roomState.users}
              radius={12}
              spacing={-2}
            />
          </div>
          {/* scheduled time and schedule button */}
          <div className="flex flex-row gap-2">
            <div className="flex flex-col">
              <strong className="text-title-large">
                <ScheduledDayOfWeekAtTime scheduledAt={scheduledAt} tt={tt} />
              </strong>
              <strong className="text-body-medium text-on-surface-var">
                <ScheduledDayOfMonthOnYear scheduledAt={scheduledAt} />
              </strong>
            </div>

            <BreakoutTooltip
              enabled={disableScheduleRoom}
              content={disabledScheduleTooltip}
            >
              <div>
                {!showLargeScheduleButton && !isReadOnly && (
                  <ScheduleRoomStateButton
                    useInlineDialog={true}
                    kind="secondary"
                    icon={<CalendarIcon size={15} />}
                    cubit={props.cubit}
                    size="small"
                    disabled={disableScheduleRoom}
                    aria-label={tt.instructor_assignment.single_group_management.schedule_meeting()}
                  />
                )}
                {showLargeScheduleButton && !isReadOnly && (
                  <ScheduleRoomStateButton
                    useInlineDialog={true}
                    kind="accent"
                    size="small"
                    cubit={props.cubit}
                    loadingText={tt.student_assignment.set_meeting_time()}
                    icon={<TriangleAlertIcon size={15} />}
                    disabled={disableScheduleRoom}
                  >
                    {tt.student_assignment.set_meeting_time()}
                  </ScheduleRoomStateButton>
                )}
                {isReadOnly && (
                  <BreakoutButton
                    kind="secondary"
                    size="small"
                    icon={<CalendarIcon size={15} />}
                    disabled={true}
                    aria-label={tt.instructor_assignment.single_group_management.cannot_schedule_meeting(
                      { status: '' }
                    )}
                  />
                )}
              </div>
            </BreakoutTooltip>
          </div>
        </div>
        {/* need background and border color for various browsers */}
        <hr className="border-fixed-accent-color bg-fixed-accent-color" />

        {/* This flex-col-reverse and .reverse() trick to avoid browser algorithms reserving space as if the chat messages appeared at the top (which would caused a scrollbar). */}
        <div className="flex min-h-0 grow flex-col-reverse overflow-y-auto px-7">
          {[...chat.messages].reverse().map((message, index) => {
            const isLast = index === 0
            if (message.type === 'nudge') {
              return (
                <SystemMessageWrapper
                  key={message.id}
                  isLast={isLast}
                  chatMessage={message}
                  chat={chat}
                >
                  <ChatNudgeMessage
                    key={index}
                    message={message}
                    className="!py-0"
                    includeDate={false}
                  />
                </SystemMessageWrapper>
              )
            }

            if (message.type === 'syntheticRoomStartedMessage')
              return (
                <SystemMessageWrapper
                  key={'syntheticRoomStartedMessage'}
                  isLast={isLast}
                  chatMessage={message}
                  chat={chat}
                >
                  <ChatRoomStartedSyntheticMessage
                    key={index}
                    roomStartedAt={message.createdAt}
                    className="!py-0"
                    includeDate={false}
                  />
                </SystemMessageWrapper>
              )

            if (message.isSystemMessage && message.systemMessagePayload) {
              const userId = message.systemMessagePayload.userId
              const user = userId
                ? props.cubit.repository.userStore.getUser(userId)
                : undefined

              return (
                <SystemMessageComponent
                  key={index}
                  systemMessage={message.systemMessagePayload}
                  chatMessage={message}
                  chat={chat}
                  user={user?.fullName ? user : undefined}
                  mountedRef={chatBoxScrollRef}
                  isLast={isLast}
                />
              )
            }

            const nextMessageAuthorId = (() => {
              // is there a next message
              if (index + 1 >= chat.messages.length) {
                return null
              }

              // next message
              const nextMessage = chat.messages[index + 1]

              // if next message and it has author id, return it
              if ('authorId' in nextMessage) {
                return nextMessage.authorId
              }

              return null
            })()

            return (
              <ChatMessageBox
                key={index}
                index={index}
                message={message}
                isLast={isLast}
                mountedRef={chatBoxScrollRef}
                chat={chat}
                roomState={roomState}
                section={section}
                nextMessageAuthorId={nextMessageAuthorId}
              />
            )
          })}
        </div>
        {!chatDisabled && (
          <form className="flex w-full flex-row gap-3" onSubmit={onFormSubmit}>
            <BreakoutTextInput
              disabled={isReadOnly}
              kind="secondary"
              ref={textInputRef}
              label={tt.student_assignment.type_messages_to_group()}
              className="w-full"
              name="chat-message"
            />
            <BreakoutButton
              size="medium"
              aria-label={tt.student_assignment.send_message()}
              disabled={isReadOnly}
              className="mt-5 aspect-square"
              icon={<SendIcon size={20} />}
              type="submit"
              kind="accent"
            />
          </form>
        )}
      </div>
    </DialogWrapper>
  )
})

const SystemMessageComponent = memo(function SystemMessageComponent({
  systemMessage,
  chatMessage,
  user,
  chat,
  mountedRef,
  isLast,
}: {
  systemMessage: SystemMessage
  chatMessage: ChatMessage
  chat: {
    markSeen: (message: ChatMessage) => void
    markAllMessagesRead: () => void
    formatDate: (date: Date) => string
    formatDateLong: (date: Date) => string
  }
  mountedRef: React.MutableRefObject<boolean>
  isLast: boolean
  user?: PublicUser
}) {
  const { tt } = useTranslationTyped()
  const ref = useRef<HTMLDivElement>(null)
  const currentUser = useBreakoutUser()
  const notifications = currentUser.notifications

  // won't render until user loaded (if we need it)
  const canScroll =
    (['join', 'leave'].includes(systemMessage.type) && user?.fullName) ||
    systemMessage.type === 'reschedule'

  useEffect(() => {
    // Let the notification system know we've seen this message
    // so that it can hide the notification
    notifications.markChatMessageAsRead(chatMessage.id)

    if (!isLast || !canScroll) return

    ref.current?.scrollIntoView?.({
      // the first time we render, we want to jump to the bottom instantly
      // after that, we want to smoothly scroll to the bottom
      behavior: mountedRef.current.valueOf() ? 'smooth' : 'instant',
      block: 'end',
    })
    mountedRef.current = true
    chat.markAllMessagesRead()
    chat.markSeen(chatMessage)
  }, [
    isLast,
    mountedRef,
    chat,
    chatMessage,
    notifications,
    canScroll,
    systemMessage.type,
  ])

  useOnVisible({
    ref,
    onVisible: () => {
      // Let the notification system know we've seen this message
      notifications.markChatMessageAsRead(chatMessage.id)
    },
  })

  if (systemMessage instanceof Error) return null

  switch (systemMessage.type) {
    case 'join':
    case 'leave': {
      if (!user || !user.fullName) return null
      return (
        <SystemMessageWrapper
          ref={ref}
          isLast={isLast}
          chatMessage={chatMessage}
          chat={chat}
        >
          <div className="flex flex-row gap-1">
            <BreakoutUserAvatar user={user} radius={10} />
            <strong
              className={classNames('text-label-medium', {
                'text-core-error': systemMessage.type === 'leave',
              })}
            >
              {systemMessage.type === 'join'
                ? tt.student_assignment.join_dialog_v2.system_messages.join({
                    name: user.fullName,
                  })
                : tt.student_assignment.join_dialog_v2.system_messages.leave({
                    name: user.fullName,
                  })}
            </strong>
          </div>
        </SystemMessageWrapper>
      )
    }
    case 'reschedule': {
      return (
        <SystemMessageWrapper
          ref={ref}
          isLast={isLast}
          chatMessage={chatMessage}
          chat={chat}
        >
          <div className="flex flex-col items-center">
            <p className="text-label-medium font-light text-core-error">
              {user?.fullName
                ? tt.student_assignment.join_dialog_v2.system_messages.rescheduled_by(
                    {
                      name: user.fullName,
                    }
                  )
                : tt.student_assignment.join_dialog_v2.system_messages.rescheduled_unknown()}
            </p>
            <p className="text-body-medium font-bold text-core-error">
              {chat.formatDateLong(systemMessage.newScheduledAt)}
            </p>
          </div>
        </SystemMessageWrapper>
      )
    }
    case 'soloBotPromptRendered': {
      return null
    }
  }
})

const SystemMessageWrapper = forwardRef<
  HTMLDivElement,
  {
    isLast: boolean
    children: React.ReactNode
    chatMessage: Pick<ChatMessage, 'createdAt'>
    chat: { formatDate: (date: Date) => string }
  }
>(function SystemMessageWrapper({ isLast, children, chatMessage, chat }, ref) {
  return (
    <div
      ref={ref}
      className={classNames(
        'flex w-full flex-row items-center justify-center',
        {
          'mb-4': !isLast,
        }
      )}
    >
      <div className="h-0.5 min-w-8 grow-[5] rounded-xl bg-surface-dim" />
      <div className="w-2 min-w-2 max-w-8 grow" />
      <div className="flex flex-col items-center">
        {children}
        {chatMessage.createdAt && (
          <p className="text-center text-body-small font-light text-on-surface-var">
            {chat.formatDate(chatMessage.createdAt)}
          </p>
        )}
      </div>
      <div className="w-2 min-w-2 max-w-8 grow" />
      <div className="h-0.5 min-w-8 grow-[5] rounded-xl bg-surface-dim" />
    </div>
  )
})

const ChatMessageBox = observer(function ChatMessageBox({
  index,
  message,
  isLast,
  mountedRef,
  chat,
  roomState,
  section,
  nextMessageAuthorId,
}: {
  index: number
  message: ChatMessage
  isLast: boolean
  mountedRef: React.MutableRefObject<boolean>
  chat: {
    markSeen: (message: ChatMessage) => void
    markAllMessagesRead: () => void
  }
  roomState: { data: { hiddenUserIds: string[]; userIds: string[] } }
  section: { data: { instructorUserId: string } }
  nextMessageAuthorId: string | null
}) {
  const { tt } = useTranslationTyped()
  const ref = useRef<HTMLDivElement>(null)
  const isCurrentUserMessage = message.isCurrentUserMessage
  const currentUser = useBreakoutUser()
  const notifications = currentUser.notifications

  enum ReadBy {
    all,
    some,
    none,
  }

  const formattedTime = message.timestamp.toLocaleTimeString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  // ta's/corre will always show up as left room if we let them send messages
  // as we can't perform any role checks
  const authorIsInRoom = useMemo(() => {
    if (isCurrentUserMessage) return true
    const { hiddenUserIds, userIds } = roomState.data

    return [
      ...hiddenUserIds,
      ...userIds,
      section.data.instructorUserId,
    ].includes(message.authorId)
  }, [
    roomState.data,
    section.data.instructorUserId,
    message.authorId,
    isCurrentUserMessage,
  ])

  const readBy = useMemo(() => {
    if (!isCurrentUserMessage) return ReadBy.none
    const seenBy = message.seenBy || {}

    // remove the author from the list of users
    const roomUserIds = roomState.data.userIds.filter(
      (uid) => uid !== message.authorId
    )

    const userIdsSet = new Set(roomUserIds)

    for (const userId of roomUserIds) {
      if (!seenBy[userId]) continue
      userIdsSet.delete(userId)
    }

    if (!userIdsSet.size) return ReadBy.all
    if (userIdsSet.size === roomUserIds.length) return ReadBy.none
    return ReadBy.some
  }, [
    ReadBy.all,
    ReadBy.none,
    ReadBy.some,
    roomState.data.userIds,
    isCurrentUserMessage,
    message.authorId,
    message.seenBy,
  ])

  useOnVisible({
    ref,
    onVisible: () => {
      // mark as seen when visible
      chat.markSeen(message)

      // Let the notification system know we've seen this message
      notifications.markChatMessageAsRead(message.id)
    },
  })

  useEffect(() => {
    if (!isLast) return

    ref.current?.scrollIntoView?.({
      // the first time we render, we want to jump to the bottom instantly
      // after that, we want to smoothly scroll to the bottom
      behavior: mountedRef.current.valueOf() ? 'smooth' : 'instant',
      block: 'end',
    })
    mountedRef.current = true

    chat.markAllMessagesRead()
  }, [isLast, mountedRef, chat])

  return (
    <div
      ref={ref}
      key={index}
      role="group"
      aria-label={tt.meeting.message_by({ name: message.author.fullName })}
      className={classNames('flex w-full flex-col gap-1', {
        'items-end': isCurrentUserMessage,
        'items-start': !isCurrentUserMessage,
        'text-right': isCurrentUserMessage,
        'mb-4': !isLast,
      })}
    >
      {/* message box */}
      <div
        className={`${
          isCurrentUserMessage ? 'bg-fixed-accent-color' : 'bg-surface-dim'
        } w-full max-w-[80%] break-words rounded-xl px-4 py-3`}
      >
        {/* message */}
        <p className="text-body-medium">
          <HyperlinkedText message={message.text} />
        </p>
        <div
          className={classNames(
            'mt-1 flex w-full flex-row items-center gap-1',
            {
              'justify-end': isCurrentUserMessage,
              'justify-start': !isCurrentUserMessage,
            }
          )}
        >
          {/* timestamp and read checks */}
          <strong className="text-body-small">{formattedTime}</strong>
          {isCurrentUserMessage && readBy === ReadBy.none && (
            <CheckIcon
              size={14}
              role="img"
              aria-label={tt.meeting.message_not_seen()}
            />
          )}
          {isCurrentUserMessage && readBy === ReadBy.some && (
            <DoubleCheckIcon
              size={14}
              role="img"
              aria-label={tt.meeting.message_seen_by_some()}
            />
          )}
          {isCurrentUserMessage && readBy === ReadBy.all && (
            <DoubleCheckIcon
              size={14}
              className="stroke-core-success"
              role="img"
              aria-label={tt.meeting.message_seen_by_all()}
            />
          )}
        </div>
      </div>
      {/* author name/ avatar */}
      {nextMessageAuthorId !== message.authorId && (
        <div
          className={classNames('flex w-full flex-row items-center gap-1', {
            'justify-start': !isCurrentUserMessage,
            'justify-end': isCurrentUserMessage,
          })}
        >
          <BreakoutUserAvatar user={message.author} radius={10} />
          <strong className="text-label-medium">
            {message.author.fullName}
          </strong>
          {!authorIsInRoom && (
            <strong className="text-label-small text-core-error">
              {tt.student_assignment.left_the_group()}
            </strong>
          )}
        </div>
      )}
    </div>
  )
})

function DialogWrapper({
  children,
  headingId,
}: {
  children: React.ReactNode
  headingId: string
}) {
  return (
    <Dialog
      testId="rooms-dialog"
      size="xl"
      className="w-[1200px]"
      innerClassName="h-[min(750px,90vh)] flex flex-col overflow-hidden max-w-[1000px] mx-auto"
      aria-labelledby={headingId}
    >
      <DialogCloseButton />
      {children}
    </Dialog>
  )
}

export const StudentAssignmentChatDialog = observer(
  function StudentAssignmentChatDialog({
    cubit,
  }: {
    cubit: StudentAssignmentCubit
  }) {
    return <GroupChatDialog mode="normal" cubit={cubit} />
  }
)
